import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Check, Crown, Zap, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { PRICING } from '../constants/pricing';
import DoneForYouCallout from '../components/DoneForYouCallout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const Upgrade = () => {
  const [processing, setProcessing] = useState(false);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const PRICE = PRICING.PRO_PRICE;
  const REGULAR_PRICE = PRICING.REGULAR_PRICE;

  const features = [
    '7-agent MOA ensemble reviews every site before you see it',
    'Groq-powered generation — full site in ~30 seconds',
    '10 color schemes × 10 industry types (100 combinations)',
    'Pexels dynamic images + Google Maps + contact form built-in',
    'One-click Netlify deploy — live URL instantly',
    'Unlimited AI chat with all local models (100% private)',
    'Content automation with blog, script & social generation',
    'Priority customer support + Mr. Support AI chatbot',
    'Lifetime access — no recurring fees, ever',
    'All future features included automatically'
  ];

  const createOrder = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/create-order`, {
        user_id: user?.id,
        amount: PRICE
      });
      return response.data.order_id;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  };

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/capture-order`, {
        order_id: data.orderID,
        user_id: user?.id
      });

      if (response.data.success) {
        // Update Supabase profile directly (user's own JWT — works with standard RLS)
        await supabase
          .from('profiles')
          .update({ subscription_status: 'paid' })
          .eq('id', user?.id);

        toast({
          title: 'Payment Successful! 🎉',
          description: 'Your account has been upgraded to PRO',
        });

        // Reload page to refresh auth context with updated profile
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: 'Please contact support',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!user) {
    // Allow non-logged in users to view pricing
    return (
      <div className="min-h-screen py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <DoneForYouCallout />
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-yellow-500/10 rounded-full mb-4">
              <Crown className="w-12 h-12 text-yellow-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">
              Self-service PRO
            </h1>
            <p className="text-xl text-gray-400">Build and manage sites yourself. Lifetime access to the self-service tools.</p>
          </div>

          <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-2xl px-8 py-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              <p className="text-yellow-200 font-semibold text-lg">
                🔥 Limited Time Launch Special - 75% OFF!
              </p>
              <Zap className="w-5 h-5 text-yellow-400" />
            </div>
          </div>

          {/* Pricing Card */}
          <Card className="bg-gray-800/50 border-gray-700 p-8 mb-8 relative overflow-hidden">
            {/* Special Offer Badge */}
            <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-bl-2xl font-bold flex items-center gap-2">
              <Zap className="w-4 h-4" />
              75% OFF - Launch Price
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center mt-8">
              {/* Pricing */}
              <div className="flex-1 text-center md:text-left">
                <div className="mb-6">
                  <div className="flex items-baseline justify-center md:justify-start gap-2 mb-2">
                    <span className="text-6xl font-bold text-white">${PRICE}</span>
                    <span className="text-gray-400">one-time</span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-2">
                    <span className="text-gray-500 line-through text-3xl">${REGULAR_PRICE}</span>
                    <span className="text-green-400 text-sm font-semibold">Save ${(REGULAR_PRICE - PRICE).toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-gray-400 text-lg mb-2">No monthly fees. Pay once, own forever.</p>
                <p className="text-yellow-400 text-sm font-semibold">⚡ Only 100 spots available at this price!</p>
              </div>

              {/* Features */}
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-4">What You Get:</h3>
                <div className="space-y-2">
                  {features.slice(0, 5).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="mt-1 bg-green-500/20 rounded-full p-1">
                        <Check className="w-3 h-3 text-green-400" />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Sign In to Purchase */}
          <Card className="bg-gray-800/50 border-gray-700 p-8 text-center">
            <h3 className="text-xl font-semibold text-white mb-4">
              Choose self-service PRO?
            </h3>
            <p className="text-gray-400 mb-6">Sign in to buy the self-service tools. This is separate from our done-for-you website service.</p>
            <Button 
              onClick={() => navigate('/auth')} 
              className="bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              Sign in for self-service PRO
            </Button>
          </Card>

          {/* All Features List */}
          <Card className="bg-gray-800/50 border-gray-700 p-6 mt-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-400" />
              Complete Feature List
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (profile?.subscription_status === 'paid') {
    return (
      <div className="min-h-screen py-12 px-6">
        <div className="max-w-md mx-auto">
          <DoneForYouCallout />
          <Card className="bg-gray-800/50 border-gray-700 p-8 max-w-md text-center">
            <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Your self-service PRO is active</h2>
            <p className="text-gray-400">Your self-service tools are unlocked. Done-for-you website orders are separate.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <DoneForYouCallout />
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-yellow-500/10 rounded-full mb-4">
            <Crown className="w-12 h-12 text-yellow-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Self-service PRO
          </h1>
          <p className="text-xl text-gray-400">Build and manage sites yourself. Lifetime access to the self-service tools.</p>
        </div>

        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 rounded-2xl px-8 py-4 mb-6">
          <div className="flex items-center justify-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            <p className="text-yellow-200 font-semibold text-lg">
              🔥 Limited Time Launch Special - 75% OFF!
            </p>
            <Zap className="w-5 h-5 text-yellow-400" />
          </div>
        </div>

        {/* Pricing Card */}
        <Card className="bg-gray-800/50 border-gray-700 p-8 mb-8 relative overflow-hidden">
          {/* Special Offer Badge */}
          <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-bl-2xl font-bold flex items-center gap-2">
            <Zap className="w-4 h-4" />
            75% OFF - Launch Price
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-center mt-8">
            {/* Pricing */}
            <div className="flex-1 text-center md:text-left">
              <div className="mb-6">
                <div className="flex items-baseline justify-center md:justify-start gap-2 mb-2">
                  <span className="text-6xl font-bold text-white">${PRICE}</span>
                  <span className="text-gray-400">one-time</span>
                </div>
                <div className="flex items-center justify-center md:justify-start gap-2">
                  <span className="text-gray-500 line-through text-3xl">${REGULAR_PRICE}</span>
                  <span className="text-green-400 text-sm font-semibold">Save ${(REGULAR_PRICE - PRICE).toFixed(2)}</span>
                </div>
              </div>
              <p className="text-gray-400 text-lg mb-2">No monthly fees. Pay once, own forever.</p>
              <p className="text-yellow-400 text-sm font-semibold">⚡ Only 100 spots available at this price!</p>
            </div>

            {/* Features */}
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-white mb-4">What You Get:</h3>
              <div className="space-y-2">
                {features.slice(0, 5).map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="mt-1 bg-green-500/20 rounded-full p-1">
                      <Check className="w-3 h-3 text-green-400" />
                    </div>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* PayPal Button */}
        <Card className="bg-gray-800/50 border-gray-700 p-8">
          <h3 className="text-xl font-semibold text-white mb-4 text-center">
            Purchase self-service PRO
          </h3>
          
          {PAYPAL_CLIENT_ID ? (
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD' }}>
              <PayPalButtons
                style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                createOrder={createOrder}
                onApprove={onApprove}
                disabled={processing}
              />
            </PayPalScriptProvider>
          ) : (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
              <p className="text-yellow-400">PayPal integration is being configured. Please check back soon!</p>
            </div>
          )}

          <p className="text-center text-gray-500 text-sm mt-4">
            🔒 Secure payment via PayPal • 💎 Instant activation
          </p>
        </Card>

        {/* All Features List */}
        <Card className="bg-gray-800/50 border-gray-700 p-6 mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            Complete Feature List
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Upgrade;